import * as React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ViewMode } from '@app/Components/CellFormatters/ViewMode';
import { Checkbox } from 'office-ui-fabric-react';
import { KeyboardKeys } from '@app/AppConstants';

type InlineCheckBoxCellProps<T extends {id: string}> = {
    value: boolean;
    row: T;
    fieldKey: keyof T;
    onChange: Function;
    isReadOnly?: boolean;
};

@observer
export class InlineCheckBoxCell<T extends {id: string}> extends React.Component<InlineCheckBoxCellProps<T>, {}> {
    store: InlineCheckBoxCellStore<T>;

    constructor(props: InlineCheckBoxCellProps<T>) {
        super(props);
        this.store = new InlineCheckBoxCellStore(props);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.store.clickHandler);
        document.removeEventListener('keydown', this.store.onKeyUp);
    }

    componentDidMount() {
        document.addEventListener('click', this.store.clickHandler);
        document.addEventListener('keydown', this.store.onKeyUp);
    }

    componentDidUpdate(prevProps: InlineCheckBoxCellProps<T>) {
        if (this.props.row.id !== prevProps.row.id || this.props.value !== prevProps.value || this.props !== prevProps
            || this.props.isReadOnly !== prevProps.isReadOnly) {
            this.store.updateProps(this.props);
        }
    }

    render() {
        const isViewMode = this.store.mode === ViewMode.view;
        const value = this.store.value;
        return (
            <div className="inline-checkbox">
                <i hidden={!isViewMode} className={`ms-Icon ms-Icon-action ms-Icon--${value ? 'Accept' : 'Cancel'}`} aria-hidden="true" onClick={this.store.toEditMode} />
                {!isViewMode &&
                <Checkbox
                    hidden={isViewMode}
                    className="mx-auto"
                    checked={this.store.value}
                    onChange={this.store.onEdit}
                />}
            </div>
        );
    }

}

class InlineCheckBoxCellStore<T extends {id: string}> {
    @observable value: boolean;
    @observable mode: string = ViewMode.view;
    private _initValue: boolean;
    private _currentProps: InlineCheckBoxCellProps<T>;

    constructor(props: InlineCheckBoxCellProps<T>) {
        this.updateProps(props);
    }

    @action
    toEditMode = (event: React.MouseEvent) => {
        if (this._currentProps.isReadOnly) return;
        this.mode = ViewMode.edit;
        event.stopPropagation();
        event.preventDefault();
    };

    @action
    onEdit = (ev: React.FormEvent<HTMLInputElement | HTMLElement> | undefined, checked: boolean | undefined): void => {
        this.value = !!checked;
    };

    @action
    clickHandler = (event: Event) => {
        let target: Element | null = event.target as HTMLElement;
        target = target.closest('.inline-checkbox');
        if (!target && this.mode === ViewMode.edit) {
            this.onBlurHandler();
        }
    };

    cancelEdit() {
        this.value = this._initValue;
        this.mode = ViewMode.view;
    }

    @action
    onKeyUp = (event: Event) => {
        const target = event as KeyboardEvent;
        if (target.keyCode === KeyboardKeys.ESCAPE) {
            this.cancelEdit();
        }
    };

    @action
    onBlurHandler = () => {
        this.mode = ViewMode.view;
        this._initValue = this.value;
        const row = this._currentProps.row as unknown as {[key: string]: boolean};
        row[this._currentProps.fieldKey as string] = this.value;
        this._currentProps.onChange(this._currentProps.row, 'contract');
    };

    @action
    updateProps(props: InlineCheckBoxCellProps<T>) {
        const value = props.value || false;
        this._initValue = value;
        this._currentProps = { ...props };
        this.value = value; //`value` prop on `input` should not be null.
    }
}